import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'

/**
 * Neutrl sNUSD — StakedUSDeV2-style ERC-4626 over NUSD, Neutrl's
 * market-neutral synthetic dollar (OTC-discount arbitrage + funding-rate
 * strategies, docs.neutrl.finance). Appreciating share price, no rebase.
 * Exit is the Ethena cooldown pattern verified on-chain 2026-08:
 * `cooldownDuration() = 864000` (10 days, governance-mutable),
 * `maxWithdraw = 0` while active, funds park in the NUSD Silo
 * (`silo()` = 0x6cdfc009…) and claim via `unstake`.
 *
 * Rate source: **on-chain vesting math** — the YieldDistributor tops the
 * vault up with newly-minted NUSD which vests linearly over
 * `vestingPeriod()` (7 days), exactly Ethena's `transferInRewards`
 * mechanism, so the instantaneous rate is
 *
 *   apr = vestingAmount / vestingPeriod × yearSeconds / totalAssets
 *
 * (`totalAssets()` already excludes the unvested remainder). When the
 * distributor is late the divisor degrades to the elapsed time since
 * `lastDistributionTimestamp`, so a stalled distribution decays instead
 * of reporting the last cycle's rate forever. Verified 2026-08-04:
 * 26,994 NUSD vesting over 35.27M → 3.99 % APR, matching Pendle's
 * independently measured `underlyingApy` of 4.09 % (APY) on the live
 * PT-sNUSD-24SEP2026 market to within compounding. Neutrl's own app API
 * (`app.neutrl.finance/api/envio`) sits behind a Vercel bot challenge
 * and DefiLlama carries no `neutrl` pool, so there is no usable
 * off-chain source — on-chain is primary by necessity as well as taste.
 */

const CHAIN_ID = Chain.ETHEREUM_MAINNET

/** sNUSD — StakedUSDeV2 clone over NUSD (Ethereum; the Arbitrum/Plasma
 *  deployments at the same address are bare LayerZero OFT bridges whose
 *  4626 surface reverts). */
const SNUSD: Address = '0x08efcc2f3e61185d0ea7f8830b3fec9bfa2ee313'

const YEAR_SECONDS = 31_536_000n
const ONE_E18 = 10n ** 18n

export const SNUSD_KEY = 'SNUSD'
/** Canonical token-list assetGroup (chains 1 / 9745 / 42161). */
export const SNUSD_GROUP_KEY = 'Staked NUSD::sNUSD'
/** Pre-unification stored form (reversed symbol::name, the mHYPER shape)
 *  — still what a not-yet-republished list resolves, so keep emitting it. */
const SNUSD_LEGACY_GROUP_KEY = 'sNUSD::Staked NUSD'

const STAKED_NUSD_ABI = [
  {
    name: 'vestingAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'lastDistributionTimestamp',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'vestingPeriod',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

const fetchSnusdAprOnChain = async (): Promise<number> => {
  const [vestingAmount, lastDistribution, vestingPeriod, totalAssets] =
    (await multicallRetryUniversal({
      chain: CHAIN_ID,
      abi: STAKED_NUSD_ABI,
      calls: [
        { address: SNUSD, name: 'vestingAmount', params: [] },
        { address: SNUSD, name: 'lastDistributionTimestamp', params: [] },
        { address: SNUSD, name: 'vestingPeriod', params: [] },
        { address: SNUSD, name: 'totalAssets', params: [] },
      ],
      allowFailure: false,
    })) as [bigint, bigint, bigint, bigint]

  if (totalAssets === 0n) return 0
  const elapsed = BigInt(Math.floor(Date.now() / 1000)) - lastDistribution
  // Nominal window while the cycle is live; the actual elapsed time once
  // it has run past its end without a new distribution.
  const window = elapsed > vestingPeriod ? elapsed : vestingPeriod
  if (window <= 0n) return 0
  const perSecond = vestingAmount / window
  return Number((perSecond * YEAR_SECONDS * ONE_E18) / totalAssets) / 1e16
}

export const snusdFetcher: YieldFetcher = {
  label: 'SNUSD',
  fetch: async () => {
    const apr = await fetchSnusdAprOnChain().catch(() => 0)
    return {
      [SNUSD_KEY]: apr,
      [SNUSD_GROUP_KEY]: apr,
      [SNUSD_LEGACY_GROUP_KEY]: apr,
    }
  },
}
